import { useState, useEffect } from 'react';
import { Calendar, Mic, ChevronRight, ChevronLeft, Sparkles, Moon } from 'lucide-react';
import VoiceRecorder from './VoiceRecorder';

const API_ORIGIN = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;

// Map mood_estimate strings from voice analysis + mood_log to a color + intensity
function moodPalette(entry) {
  if (!entry) return { bg: 'var(--surface-2)', border: 'var(--border-soft)', label: '' };
  const est = (entry.mood_estimate || '').trim();
  const risk = entry.risk_score || 0;
  const score = entry.mood_score;

  // Crisis / high risk takes priority
  if (risk >= 7) return { bg: 'rgba(229,133,133,0.55)', border: '#c46464', label: est || 'خطر' };
  if (risk >= 4) return { bg: 'rgba(245,184,146,0.55)', border: '#e0965a', label: est || 'انتباه' };

  // Mood-based
  if (est.includes('مرتفع') || est.includes('هوس') || est.includes('نشوة')) return { bg: 'rgba(240,177,108,0.55)', border: '#c9871f', label: est };
  if (est.includes('منخفض') || est.includes('اكتئاب') || est.includes('حزن')) return { bg: 'rgba(139,127,240,0.35)', border: '#8b7ff0', label: est };
  if (est.includes('قلق') || est.includes('توتر')) return { bg: 'rgba(245,184,146,0.35)', border: '#f5b892', label: est };
  if (est.includes('مختلط') || est.includes('متذبذ')) return { bg: 'linear-gradient(135deg, rgba(240,177,108,0.4), rgba(139,127,240,0.4))', border: '#a5a5ea', label: est };

  // By numeric score (1-6)
  if (score != null) {
    if (score <= 1) return { bg: 'rgba(139,127,240,0.35)', border: '#8b7ff0', label: est || 'صعب' };
    if (score <= 2) return { bg: 'rgba(139,127,240,0.22)', border: '#b8afff', label: est || 'منخفض' };
    if (score <= 3) return { bg: 'var(--surface-2)', border: 'var(--border)', label: est || 'عادي' };
    if (score <= 4) return { bg: 'rgba(126,210,183,0.35)', border: '#5eb2a3', label: est || 'جيد' };
    if (score <= 5) return { bg: 'rgba(126,210,183,0.55)', border: '#5eb2a3', label: est || 'إيجابي' };
    return { bg: 'rgba(240,197,116,0.55)', border: '#c9871f', label: est || 'مرتفع جداً' };
  }

  return { bg: 'rgba(126,210,183,0.28)', border: '#a4d6cc', label: est || 'هادئ' };
}

function shortDayName(d) {
  return ['أحد', 'اثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'][d.getDay()];
}

function monthLabelAr(y, m) {
  const names = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
  return `${names[m - 1]} ${y}`;
}

export default function EmotionalMap({ patient, onOpenDiaryRecorder }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState({ days: {}, total_days: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showRecorder, setShowRecorder] = useState(false);

  useEffect(() => { load(); }, [year, month]);

  const load = async () => {
    setLoading(true);
    try {
      const mm = `${year}-${String(month).padStart(2, '0')}`;
      const r = await fetch(`${API_ORIGIN}/api/patients/${patient.id}/emotional-map?month=${mm}`);
      const d = await r.json();
      setData(d || { days: {}, total_days: 0 });
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const todayKey = new Date().toISOString().slice(0, 10);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else { setMonth(month - 1); }
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else { setMonth(month + 1); }
  };

  // Build calendar grid (7 columns, Sun first)
  const cells = [];
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const entry = data.days[key] || null;
    cells.push({ key, day: d, entry });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const selectedEntry = selected ? data.days[selected] : null;
  const filledDays = data.total_days;
  const pct = Math.round((filledDays / daysInMonth) * 100);

  return (
    <div className="rounded-3xl p-4 mb-4" style={{ background: 'linear-gradient(135deg, #faf6f2, #f5efff)', border: '1px solid var(--border-soft)' }} data-testid="emotional-map">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--primary-dark)' }} />
            <span className="text-[10.5px] font-extrabold uppercase tracking-wider" style={{ color: 'var(--primary-dark)' }}>خارطة عاطفية</span>
          </div>
          <div className="text-[15px] font-black" style={{ color: 'var(--text)' }}>{monthLabelAr(year, month)}</div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={prevMonth} className="w-8 h-8 rounded-xl bg-white border border-[var(--border-soft)] hover:border-[var(--primary)] flex items-center justify-center" data-testid="em-prev-month">
            <ChevronRight className="w-3.5 h-3.5" style={{ color: 'var(--text-2)' }} />
          </button>
          <button onClick={nextMonth} className="w-8 h-8 rounded-xl bg-white border border-[var(--border-soft)] hover:border-[var(--primary)] flex items-center justify-center" data-testid="em-next-month">
            <ChevronLeft className="w-3.5 h-3.5" style={{ color: 'var(--text-2)' }} />
          </button>
        </div>
      </div>

      {/* Progress line */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10.5px] mb-1" style={{ color: 'var(--text-2)' }}>
          <span>{filledDays} من {daysInMonth} يوم</span>
          <span className="font-extrabold" style={{ color: 'var(--primary-dark)' }}>{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-soft)' }}>
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #8b7ff0, #7dd3c3)' }}></div>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-1.5">
        {['أحد', 'اثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'].map((d) => (
          <div key={d} className="text-center text-[9.5px] font-extrabold" style={{ color: 'var(--text-3)' }}>{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="aspect-square"></div>;
          const p = moodPalette(c.entry);
          const isToday = c.key === todayKey;
          const isSelected = selected === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setSelected(isSelected ? null : c.key)}
              disabled={!c.entry}
              className={`aspect-square rounded-xl relative flex items-center justify-center text-[11px] font-extrabold transition-all ${c.entry ? 'hover:scale-105 shadow-sm' : 'opacity-60'} ${isSelected ? 'ring-2 ring-offset-1' : ''} ${isToday ? 'ring-2' : ''}`}
              style={{
                background: c.entry ? p.bg : 'transparent',
                border: `1.5px solid ${c.entry ? p.border : 'var(--border-soft)'}`,
                color: 'var(--text)',
                ...(isSelected ? { '--tw-ring-color': p.border } : {}),
                ...(isToday && !isSelected ? { '--tw-ring-color': 'var(--primary)' } : {}),
              }}
              data-testid={`em-day-${c.day}`}
            >
              <span>{c.day}</span>
              {c.entry && c.entry.source === 'voice' && (
                <Mic className="absolute top-0.5 right-0.5 w-2 h-2 opacity-60" strokeWidth={3} />
              )}
              {c.entry && c.entry.risk_score >= 7 && (
                <div className="absolute bottom-0.5 left-0.5 w-1.5 h-1.5 rounded-full bg-white border border-[var(--danger)]"></div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected day detail */}
      {selectedEntry && (
        <div className="mt-3 rounded-2xl p-3.5" style={{ background: 'white', border: '1px solid var(--border-soft)' }} data-testid="em-day-detail">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: moodPalette(selectedEntry).bg, border: `1px solid ${moodPalette(selectedEntry).border}` }}>
              {selectedEntry.source === 'voice' ? <Mic className="w-3 h-3" style={{ color: moodPalette(selectedEntry).border }} /> : <Sparkles className="w-3 h-3" style={{ color: moodPalette(selectedEntry).border }} />}
            </div>
            <div>
              <div className="text-[12px] font-extrabold" style={{ color: 'var(--text)' }}>{selected}</div>
              <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>{moodPalette(selectedEntry).label || '—'}{selectedEntry.mood_score != null ? ` · ${selectedEntry.mood_score}/6` : ''}{selectedEntry.risk_score >= 4 ? ` · خطورة ${selectedEntry.risk_score}/10` : ''}</div>
            </div>
          </div>
          {selectedEntry.transcript_snippet && (
            <div className="text-[12.5px] leading-relaxed mb-1.5" style={{ color: 'var(--text)' }}>« {selectedEntry.transcript_snippet}{selectedEntry.transcript_snippet.length >= 180 ? '…' : ''} »</div>
          )}
          {selectedEntry.ai_note && (
            <div className="text-[11px] italic mt-1 pt-1.5 border-t border-[var(--border-soft)]" style={{ color: 'var(--text-2)' }}>{selectedEntry.ai_note}</div>
          )}
        </div>
      )}

      {/* Tonight's diary CTA */}
      <button
        onClick={() => setShowRecorder(true)}
        className="w-full mt-3 rounded-2xl p-3.5 flex items-center gap-3 shadow-md active:scale-[0.98] transition-transform"
        style={{ background: 'linear-gradient(135deg, #6a5cd8, #8b7ff0)' }}
        data-testid="em-record-diary-btn"
      >
        <div className="w-10 h-10 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center flex-shrink-0">
          <Moon className="w-4.5 h-4.5 text-white" strokeWidth={2.4} />
        </div>
        <div className="flex-1 text-right">
          <div className="text-[13px] font-extrabold text-white">مذكرة الليلة</div>
          <div className="text-[10.5px] text-white/85">٣٠ ثانية عن يومك — سنلوّنها هنا</div>
        </div>
      </button>

      {/* Legend */}
      {loading && <div className="text-center text-[10.5px] mt-2" style={{ color: 'var(--text-3)' }}>يتحمل…</div>}

      <VoiceRecorder
        open={showRecorder}
        mode="session"
        sessionMinutes={2}
        patient={patient}
        onClose={() => { setShowRecorder(false); load(); }}
      />
    </div>
  );
}
